import React, { useCallback, useEffect, useState } from 'react';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Remove from '@mui/icons-material/Remove';
import ShoppingBagOutlined from '@mui/icons-material/ShoppingBagOutlined';
import {
  Box,
  Card,
  CardMedia,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import noImage from '../assets/no-image.png';
import Button from '../components/Button';
import PageContainer from '../layouts/PageContainer';
import { useV2Common } from '../state/CommonProvider';
import {
  getCart,
  setCartInstructions,
  setCartLineQuantity,
  formatCartCustomizationSummary,
} from '../services/commerce';

function CartLineRow({ line, onQtyChange, onRemoveLine, onOpenProduct, busy }) {
  const qty = Number(line.quantity) || 0;
  const unit = Number(line.price) || 0;
  const lineTotal = unit * qty;
  const variantLabel = line.variant?.weight ? String(line.variant.weight) : null;
  const customText = formatCartCustomizationSummary(line.customizations);
  const detail = [variantLabel, customText].filter(Boolean).join(' · ');

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        mb: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5, p: 1.5 }}>
        <CardMedia
          component="img"
          image={line.imageUrl || noImage}
          alt={line.name}
          sx={{
            width: 88,
            height: 88,
            flexShrink: 0,
            borderRadius: 1,
            objectFit: 'cover',
            bgcolor: 'grey.100',
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = noImage;
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ cursor: 'pointer', lineHeight: 1.3 }}
            onClick={() => line.productId && onOpenProduct?.(line.productId)}
          >
            {line.name}
          </Typography>
          {detail ? (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
              {detail}
            </Typography>
          ) : null}
          <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
            ₹{unit} 
            {qty > 1 ? ` · ₹${lineTotal}` : null}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '44px 1fr 44px',
              alignItems: 'center',
              justifyItems: 'center',
              width: '100%',
              maxWidth: 200,
              mt: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '5px',
              px: 0.5,
              py: 0.25,
              minHeight: 40,
            }}
          >
            <IconButton
              size="small"
              aria-label="Decrease quantity"
              disabled={busy || qty <= 0}
              onClick={() => onQtyChange(line.productId, qty - 1)}
            >
              <Remove fontSize="small" />
            </IconButton>
            <Typography variant="body1" fontWeight={800}>
              {qty}
            </Typography>
            <IconButton
              size="small"
              aria-label="Increase quantity"
              disabled={busy}
              onClick={() => onQtyChange(line.productId, qty + 1)}
            >
              <Add fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <IconButton
          aria-label="Remove from bag"
          size="small"
          disabled={busy}
          onClick={() => onRemoveLine(line.productId)}
          sx={{ alignSelf: 'flex-start', color: 'text.secondary' }}
        >
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Box>
    </Card>
  );
}

export default function Cart() {
  const navigate = useNavigate();
  const { isDesktop } = useV2Common();
  const [cart, setCart] = useState({ products: [], totalAmount: 0, totalCount: 0 });
  const [instructionInput, setInstructionInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadCart = useCallback(async () => {
    const next = await getCart();
    setCart({
      products: Array.isArray(next.products) ? next.products : [],
      totalAmount: Number(next.totalAmount) || 0,
      totalCount: Number(next.totalCount) || 0,
    });
    setInstructionInput(typeof next.instructions === 'string' ? next.instructions : '');
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await loadCart();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadCart]);

  useEffect(() => {
    const onCartChanged = () => {
      loadCart();
    };
    window.addEventListener('v2-cart-changed', onCartChanged);
    return () => window.removeEventListener('v2-cart-changed', onCartChanged);
  }, [loadCart]);

  const handleQtyChange = async (productId, nextQty) => {
    if (!productId) return;
    setBusy(true);
    try {
      await setCartLineQuantity({ productId }, nextQty);
      await loadCart();
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (productId) => {
    await handleQtyChange(productId, 0);
  };

  const goToProduct = (productId) => {
    navigate(`/v2/catalog/${productId}`);
  };

  const hasItems = cart.products.length > 0 && cart.totalCount > 0;

  if (loading) {
    return (
      <PageContainer title="Cart">
        <Typography variant="body2" color="text.secondary">
          Loading your bag…
        </Typography>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Cart">
      {!hasItems ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            py: 6,
            px: 2,
          }}
        >
          <ShoppingBagOutlined sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Your bag is empty
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 280 }}>
            Add items from the catalog to see them here.
          </Typography>
          <Button onClick={() => navigate('/v2/catalog')}>Browse catalog</Button>
        </Box>
      ) : (
        <>
          <Box sx={{ pb: { xs: 12, md: 10 } }}>
            {cart.products.map((line) => (
              <CartLineRow
                key={String(line.productId)}
                line={line}
                onQtyChange={handleQtyChange}
                onRemoveLine={handleRemove}
                onOpenProduct={goToProduct}
                busy={busy}
              />
            ))}

            <Box
              sx={{
                p: 1.5,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                mb: 2,
              }}
            >
              <TextField
                placeholder="Add instructions if any (max 100 characters)"
                multiline
                minRows={2}
                maxRows={4}
                fullWidth
                value={instructionInput}
                inputProps={{ maxLength: 100 }}
                onChange={(e) => setInstructionInput(e.target.value)}
                onBlur={() => {
                  setCartInstructions(instructionInput).catch(() => {});
                }}
                disabled={busy}
                size="small"
              />
            </Box>
          </Box>

          <Box
            sx={(theme) => ({
              position: 'fixed',
              zIndex: theme.zIndex.appBar,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '5px',
              px: 2,
              py: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              boxShadow: 3,
              ...(isDesktop
                ? {
                    left: '50%',
                    transform: 'translateX(-50%)',
                    right: 'auto',
                    width: 'min(720px, calc(100vw - 32px))',
                    bottom: theme.spacing(2),
                  }
                : {
                    left: theme.spacing(2),
                    right: theme.spacing(2),
                    bottom: `calc(${theme.spacing(8)} + env(safe-area-inset-bottom, 0px))`,
                  }),
            })}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Total
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {cart.totalCount} {cart.totalCount === 1 ? 'item' : 'items'}
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight={800}>
                ₹{cart.totalAmount}
              </Typography>
            </Box>
            <Button
              fullWidth
              size="large"
              onClick={async () => {
                try {
                  await setCartInstructions(instructionInput);
                } catch {
                  // still navigate; checkout reads latest cache when possible
                }
                navigate('/v2/checkout');
              }}
              sx={{ borderRadius: '5px' }}
            >
              Checkout
            </Button>
          </Box>
        </>
      )}
    </PageContainer>
  );
}
